import { formatDate } from "src/utils/string/stringUtil";
import { defaultEQM2040_R01Dto, EQM2040_R01Dto } from "./Dto_EQM2040_R01";
import type { EQM2040_R01Filter } from "./Store_EQM2040_R01";

/**
 * 기본 분석일자 생성 함수
 * @param filter - EQM2040_R01Filter (searchAnalysisDt 포함)
 * @returns yyyy-MM-dd 형식의 날짜 문자열
 */
export const buildDefaultInspectionDt = (filter?: EQM2040_R01Filter | null): string => {
	if (!filter?.inspectionMonth) {
		return formatDate(new Date());
	}

	// searchAnalysisDt 형식: yyyy-MM
	const parts = filter.inspectionMonth.split('-');
	if (parts.length !== 2) {
		// 잘못된 형식인 경우 오늘 날짜 반환
		return formatDate(new Date());
	}

	const [year, month] = parts;
	// 해당 월의 첫 번째 날짜 반환 (yyyy-MM-dd 형식)
	return `${year}-${month}-01`;
};

export const getFilterYearMonth = (filter: EQM2040_R01Filter): string => {
	const inspectionDt = filter.inspectionMonth;
	if (/^\d{4}-\d{2}$/.test(inspectionDt)) {
		return inspectionDt;
	}

	return buildDefaultInspectionDt(filter).slice(0, 7);
};

export const buildMonthlyInspectionRows = (
	filter: EQM2040_R01Filter,
	responseData: EQM2040_R01Dto[]
): { displayList: EQM2040_R01Dto[]; originalList: EQM2040_R01Dto[] } => {
	const yearMonth = getFilterYearMonth(filter);
	const [year, month] = yearMonth.split('-').map(Number);
	const daysInMonth = new Date(year, month, 0).getDate();
	
	const responseMap = new Map(responseData.map(item => [item.inspectionDt?.slice(0, 10), item]));

	const displayList = Array.from({ length: daysInMonth }, (_, index) => {
		const day = String(index + 1).padStart(2, '0');
		const inspectionDt = `${yearMonth}-${day}`;
		const responseItem = responseMap.get(inspectionDt);
		const rowIndex = index + 1;

		return {
			...defaultEQM2040_R01Dto,
			compCd: filter.compCd || responseItem?.compCd || defaultEQM2040_R01Dto.compCd,
			...responseItem,
			valueIndex: rowIndex,
			orderIndex: rowIndex,
			orderIndexSeq: 0,
			inspectionDt,
			inserted: false,
			isModified: false,
		};
	});

	const originalList = displayList.map(row => ({
		...row,
		inserted: !responseMap.has(row.inspectionDt),
	}));

	return { displayList, originalList };
};

/**
 * valueIndex 정규화 함수
 * - 유효한 양수면 그대로 반환
 * - 그렇지 않으면 fallbackIndex 반환
 * @param valueIndex - 원본 valueIndex
 * @param fallbackIndex - 대체 값 (보통 배열 인덱스 + 1)
 * @returns 정규화된 valueIndex
 * @example
 * normalizeValueIndex(5, 1) // 5 (유효한 값)
 * normalizeValueIndex(0, 1) // 1 (0은 무효, fallback 사용)
 * normalizeValueIndex(null, 3) // 3 (null은 무효, fallback 사용)
 */
export const normalizeValueIndex = (
	valueIndex: number | undefined | null,
	fallbackIndex: number
): number => {
	return (valueIndex && valueIndex > 0) 
		? Number(valueIndex) 
		: fallbackIndex;
};

/**
 * analysisDt에서 일자만 추출하는 함수
 * @param analysisDt - yyyy-MM-dd 형식의 날짜 문자열
 * @returns 일자 (dd 형식) 또는 빈 문자열
 * @example
 * extractDayFromAnalysisDt('2024-01-15') // '15'
 * extractDayFromAnalysisDt('2024-12-05') // '05'
 * extractDayFromAnalysisDt('invalid') // ''
 */
export const extractDayFromInspectionDt = (inspectionDt: string): string => {
	if (!inspectionDt) return '';
	const parts = inspectionDt.split('-');
	return parts.length === 3 ? parts[2] : '';
};

